import {
  Box,
  Button,
  Divider,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { useEffect, useRef, useState } from "react";
import { InventoryItemsTable } from "components/Inventory/InventoryItemsTable";
import { useSnackbar } from "notistack";
import { useGetItemsDropdownService } from "subModule/src/services/items/useItems";
import { selectUserInfo } from "features/user/userSlice";
import { useSelector } from "react-redux";
import AlertMessages from "common/Alert/AlertMessages";
import { useNavigate, useParams } from "react-router-dom";
import {
  InventoryProfileService,
  ItemsProfileService,
  RackProfileService,
} from "subModule/src/core/services";
import {
  GetStockTransferByIdResponse,
  StockTransfer,
} from "subModule/src/services/inventory/interface";
import Loader from "layout/Loader";
import { useTranslation } from "react-i18next";
import BasicDatePicker from "common/Pickers/Pickers";
import { useGetStoreDropdownService } from "subModule/src/services/store/useStore";
import DynamicSelect from "common/Select/Select";
import dayjs from "dayjs";
import AddIcon from "@mui/icons-material/Add";
import { themes } from "theme/themes";
import { brandingInfo } from "features/server/brandingSlice";
import { GetItemsDropdownResponse } from "subModule/src/services/items/interface";
import ComponentHeaderTwo from "common/ComponentHeaderTwo/ComponentHeaderTwo";
import InventoryIcon from "@mui/icons-material/Inventory";
import { useBusinessSetupEntityBreadcrumbs } from "Helpers/useBusinessSetupEntityBreadcrumbs";
import { normalizeBinsList } from "subModule/src/services/rack/service";
import { isBinInventoryMode } from "pages/Store/Racks/rackUtils";

interface FormData {
  [key: string]: any;
}

const StockTransferCreateUpdate: React.FC = () => {
  const params = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { breadcrumbItems, captureLabel } = useBusinessSetupEntityBreadcrumbs({
    sectionLabelKey: "Inventory",
    moduleLabelKey: "StockTransfers",
    moduleHref: "/inventory/stock-transfers",
    entityFallbackLabelKey: "StockTransfer",
    isEdit: !!params?.id,
    action: "details",
  });
  const loggedInUserCurrency = useSelector(selectUserInfo)?.business?.currency;
  const { enqueueSnackbar } = useSnackbar();
  const branding = useSelector(brandingInfo);
  const themeColors =
    branding?.attributes?.themeId === 100
      ? branding?.attributes?.customTheme
      : (themes as any)[branding?.attributes?.themeId || 3];
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);
  const {
    control,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    watch,
    resetField,
  } = useForm<FormData>({
    defaultValues: {
      stockTransferItem: [],
      transferDate: dayjs(new Date())?.format("YYYY-MM-DD"),
    },
  });
  const [dropdownSearchTerm, setDropdownSearchTerm] = useState<any>("");
  const [dropdownPage, setDropdownPage] = useState<number>(1);
  const [searchLoading, setSearchLoading] = useState<string | null>(null);
  const { data: itemsData, refetch: itemsRefetch } = useGetItemsDropdownService(
    {
      pageNumber: dropdownPage,
      size: 20,
      storeId: watch("sourceStoreId")?.value,
      search: dropdownSearchTerm,
    },
  );
  const { data: storesData, refetch: storesRefetch } =
    useGetStoreDropdownService({
      pageNumber: dropdownPage,
      size: 50,
      title: dropdownSearchTerm,
    });
  const [stockTransferItem, setStockTransferItem] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(params?.id ? true : false);
  const [stockTransfer, setStockTransfer] = useState<StockTransfer>();
  const [IsBtnEnabled, setIsBtnEnabled] = useState<boolean>(false);
  const [destinationBins, setDestinationBins] = useState<
    { value: number; label: string }[]
  >([]);
  const destinationStore = watch("destinationStoreId");
  const isDestinationBinMode = isBinInventoryMode(
    destinationStore?.inventoryMode,
  );
  useEffect(() => {
    storesRefetch();
    if (params?.id) {
      if (loading) getStockTransferById();
    }
  }, [params]);
  useEffect(() => {
    if (watch("sourceStoreId")?.value) {
      setLoading(true);
      setStockTransferItem([]);
      setValue("stockTransferItem", []);
      itemsRefetch()
        .then((res: GetItemsDropdownResponse | unknown | any) => {
          if (res?.data?.pages[0]?.success) {
            ///
          } else {
            enqueueSnackbar(
              <AlertMessages
                text={res?.data?.message || "Something went wrong"}
              />,
              { variant: "error" },
            );
          }
        })
        .catch((err) => {
          console.error("===", err);
        })
        .finally(() => setLoading(false));
    }
  }, [watch("sourceStoreId")]);
  const getStockTransferById = () => {
    InventoryProfileService.getStockTransferById(params?.id)
      .then((res: any) => {
        if (res.success) {
          setLoading(false);
          setStockTransfer(res.data as any);
          const source = (res.data as any)?.sourceStoreTitle || "";
          const dest = (res.data as any)?.destinationStoreTitle || "";
          captureLabel(
            source && dest
              ? `${source} → ${dest}`
              : source || dest || (params?.id ? String(params.id) : null),
          );
          setStockTransferItem((res.data as any)?.stockTransferItem || []);
          setValue("stockTransferItem", res.data.stockTransferItem || []);
          setValue("sourceStoreId", res.data?.sourceStoreId);
          setValue("destinationStoreId", res.data?.destinationStoreId);
          setValue("id", params?.id);
          setValue("transferDate", res.data?.transferDate?.split(" ")[0]);
          setValue("remark", res.data.remark || "");
          setLoading(false);
        } else {
          enqueueSnackbar(
            <AlertMessages text={res.message || "Something went wrong"} />,
            { variant: "error" },
          );
        }
      })
      .catch((err: any) => {
        console.error("===", err);
        setLoading(false);
      });
  };
  const storeOptions = (storesData?.pages[0]?.data?.content || []).map(
    (item: { id: number; title: string; inventoryMode?: number | null }) => ({
      value: item.id,
      label: item.title,
      inventoryMode: item.inventoryMode ?? null,
    }),
  );
  const destinationStores = storeOptions.filter(
    (option) => option.value !== watch("sourceStoreId")?.value,
  );
  const loadDestinationBins = (storeId: number) => {
    if (!storeId) {
      setDestinationBins([]);
      return Promise.resolve([]);
    }
    setSearchLoading("bins");
    return RackProfileService.getBinsByStore(storeId)
      .then((res: any) => {
        const list = normalizeBinsList(res).map((bin) => ({
          value: bin.id,
          label: bin.locationCode || String(bin.id),
        }));
        setDestinationBins(list);
        return list;
      })
      .catch((err: any) => {
        console.error("===", err);
        setDestinationBins([]);
        return [];
      })
      .finally(() => setSearchLoading(null));
  };
  const resolveDestinationBinOption = (
    locationRes: any,
    bins: { value: number; label: string }[],
  ) => {
    const data = locationRes?.data;
    if (!data) return null;
    const binId = Number(data.binId ?? data.id) || null;
    const locationCode = data.locationCode || data.code || "";
    if (binId) {
      return (
        bins.find((b) => b.value === binId) || {
          value: binId,
          label: locationCode || String(binId),
        }
      );
    }
    if (locationCode) {
      return bins.find((b) => b.label === locationCode) || null;
    }
    return null;
  };
  const fetchItemDestinationLocation = async (
    index: number,
    itemId: number,
    storeId: number,
    bins?: { value: number; label: string }[],
  ) => {
    if (!itemId || !storeId) return;
    try {
      const res = await ItemsProfileService.getItemLocation(storeId, itemId);
      const option = resolveDestinationBinOption(res, bins || destinationBins);
      setStockTransferItem((prev) => {
        const next = [...prev];
        if (!next[index]) return prev;
        next[index] = { ...next[index], destinationBinId: option };
        return next;
      });
      setValue(`stockTransferItem.${index}.destinationBinId`, option);
    } catch (err) {
      console.error("===", err);
      setStockTransferItem((prev) => {
        const next = [...prev];
        if (!next[index]) return prev;
        next[index] = { ...next[index], destinationBinId: null };
        return next;
      });
      setValue(`stockTransferItem.${index}.destinationBinId`, null);
    }
  };
  const applyDestinationLocations = async (
    storeId: number,
    items: any[],
    bins: { value: number; label: string }[],
  ) => {
    await Promise.all(
      items.map(async (item, index) => {
        const itemId = item?.itemId || item?.itemName?.value;
        if (!itemId) {
          setValue(`stockTransferItem.${index}.destinationBinId`, null);
          return;
        }
        await fetchItemDestinationLocation(
          index,
          Number(itemId),
          storeId,
          bins,
        );
      }),
    );
  };
  const handleDestinationStoreChange = async (selectedOption: any) => {
    setValue("destinationStoreId", selectedOption);
    const clearedItems = (stockTransferItem || []).map((item) => ({
      ...item,
      destinationBinId: null,
    }));
    setStockTransferItem(clearedItems);
    clearedItems.forEach((_, index) => {
      setValue(`stockTransferItem.${index}.destinationBinId`, null);
    });
    if (
      !selectedOption?.value ||
      !isBinInventoryMode(selectedOption?.inventoryMode)
    ) {
      setDestinationBins([]);
      return;
    }
    const bins = await loadDestinationBins(selectedOption.value);
    await applyDestinationLocations(selectedOption.value, clearedItems, bins);
  };
  const columns = [
    { id: "itemId" },
    { id: "itemCode" },
    { id: "unitId" },
    {
      id: "itemName",
      label: t("Item") + " *",
      numeric: false,
      disablePadding: false,
      type: "text",
    },
    ...(params?.id
      ? []
      : [{ id: "baseItemQuantity", label: t("availableQuantity") }]),
    ...(params?.id ? [] : [{ id: "locationCode", label: t("location") }]),
    {
      id: "quantity",
      label: t("Quantity"),
      numeric: false,
      disablePadding: false,
      type: "number",
      field: "quantity",
    },
    { id: "unitTitle" },
    ...(!params?.id && isDestinationBinMode
      ? [
          {
            id: "destinationBinId",
            label: t("BIN"),
            numeric: false,
            type: "select",
          },
        ]
      : []),
  ];
  const DeleteItem = (row: any) => {
    setStockTransferItem((prevRows) => {
      const newRows = prevRows.filter((rows) => rows.id !== row.id);
      setValue("stockTransferItem", []);
      setTimeout(
        () =>
          setValue("stockTransferItem", newRows, {
            shouldValidate: true,
            shouldDirty: true,
          }),
        0,
      );
      return newRows;
    });
  };
  const addNewItem = (typeName: string) => {
    if (typeName === "stockTransferItem") {
      const newItem = columns.reduce(
        (acc, column) => ({
          ...acc,
          [column.id]: column.type === "number" ? 0 : "",
        }),
        {},
      );
      if (!params?.id) (newItem as any).id = Date.now();
      setStockTransferItem((prevRows) => [...prevRows, newItem]);
    }
  };
  const handleInputChange = (
    index: number,
    columnId: string,
    value: any,
    typeName: string,
    type: string,
  ) => {
    if (typeName === "stockTransferItem") {
      const updatedItems = [...stockTransferItem];
      updatedItems[index][columnId] =
        type === "number" ? parseFloat(value) : value;
      setStockTransferItem(updatedItems);
    }
  };
  const onSubmit = (data: FormData) => {
    if (Object.values(data)?.length) {
      setIsBtnEnabled(true);
      const filteredItems = stockTransferItem.filter(
        (item: any) => item?.itemName,
      );
      if (filteredItems.length === 0) {
        enqueueSnackbar(<AlertMessages text={t("addAtLeastOneItem")} />, {
          variant: "error",
        });
        setIsBtnEnabled(false);
        return;
      }
      const payloadItems = filteredItems.map((row: any) => {
        const destinationBinId =
          row?.destinationBinId?.value ?? row?.destinationBinId ?? null;
        return {
          itemId: row.itemId,
          itemCode: row.itemCode,
          unitId: row.unitId,
          itemName: row.itemName,
          baseItemQuantity: row.baseItemQuantity ?? 0,
          quantity: row.quantity,
          ...(isDestinationBinMode && destinationBinId
            ? { destinationBinId: Number(destinationBinId) }
            : {}),
        };
      });
      InventoryProfileService.createStockTransfer({
        ...data,
        sourceStoreId: data.sourceStoreId.value,
        destinationStoreId: data.destinationStoreId.value,
        stockTransferItem: payloadItems,
      })
        .then((response: GetStockTransferByIdResponse) => {
          if (response.success) {
            InventoryProfileService.changeStatusEntity({
              typeName: "stocktransfer",
              entityId: response?.data?.id,
              status: 2,
            })
              .then((res: any) => {
                if (res.success) {
                  enqueueSnackbar(
                    <AlertMessages
                      statusCode={res.status}
                      text={
                        t("StockTransfer") +
                        " " +
                        t("saved") +
                        " " +
                        t("successfully")
                      }
                    />,
                    { variant: "success" },
                  );
                  navigate("/inventory/stock-transfers");
                } else {
                  enqueueSnackbar(<AlertMessages text={t(res?.message)} />, {
                    variant: "error",
                  });
                }
              })
              .catch((err: any) => {
                console.error("===", err);
                enqueueSnackbar(
                  <AlertMessages
                    text={
                      err?.message ||
                      err?.data?.message ||
                      "Something went wrong"
                    }
                  />,
                  { variant: "error" },
                );
              });
          } else
            enqueueSnackbar(<AlertMessages text={t(response?.message)} />, {
              variant: "error",
            });
        })
        .catch((err: any) => {
          console.error("===", err);
          enqueueSnackbar(
            <AlertMessages
              text={t(
                err?.message || err?.data?.message || "Something went wrong",
              )}
            />,
            { variant: "error" },
          );
        })
        .finally(() => setIsBtnEnabled(false));
    }
  };
  const handleDropdownActions = (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => {
    if (action === "searchIt") {
      setDropdownPage(1);
      setDropdownSearchTerm(search);
      if (search) setSearchLoading(typeName);
    } else {
      setDropdownPage(dropdownPage + 1);
      setSearchLoading(typeName);
    }
    if (debounceTimer.current) clearTimeout(debounceTimer.current);
    debounceTimer.current = setTimeout(() => {
      if (typeName === "stores" || typeName === "destinationStores")
        storesRefetch().finally(() => setSearchLoading(null));
      else itemsRefetch().finally(() => setSearchLoading(null));
    }, 300);
  };
  return (
    <>
      <ComponentHeaderTwo
        breadcrumbItems={breadcrumbItems}
        showBtn={true}
        showSaveBtn={false}
        onClick={() => navigate("/inventory/stock-transfers")}
        onSubmit={() => {}}
        heading={t("StockTransfers")}
        icon={
          <InventoryIcon sx={{ fill: "#000", width: "28px", height: "28px" }} />
        }
        btnText={"back"}
        btnType={"back"}
        showCreateBtn={false}
      />
      <Grid
        container
        component="form"
        id="form"
        className="px-[8px] items-baseline"
        rowGap={2.5}
        columnGap={4}
        onSubmit={handleSubmit(onSubmit)}
      >
        <Grid container className="items-center">
          {/* <Grid item xs={12} sm={6} className="text-start py-0"> */}
          <Typography
            className="font-Saira text-[15px] font-semibold"
            style={{ color: themeColors?.primary }}
          >
            {params?.id ? t("StockTransfer") : t("StockTransfer")}{" "}
            {t("Details")}
          </Typography>
          {/* </Grid> */}
          {/* <Grid item xs={12} sm={6} className="hide-on-print">
            <div className="flex gap-2 justify-end">
                        {params?.id ? null : <Button type="submit" disabled={IsBtnEnabled} className="primary" style={{ background: themeColors?.primary }} variant="contained" disableElevation onClick={() => onSubmit(handleSubmit(onSubmit))}>{t('save')}</Button>}
                        {params?.id ? <> <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button>
                        <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation onClick={() => navigate(`${window.location.pathname}/print`)}>{t('Print')}</Button></> : null}
                        <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation onClick={() => navigate('/inventory/stock-transfers')}>{t('Back to list')}</Button>
                    </div>
          </Grid> */}
        </Grid>
        {/* <Grid item xs={12} className="py-0">
          <Divider className="bg-[#AEB3C4] hide-on-print" />
        </Grid> */}
        {params?.id ? (
          <Grid
            container
            className="py-[12px] px-[18px] rounded-[8px] mb-[16px] items-center"
            style={{ background: themeColors?.primary }}
          >
            <Grid item xs={4}>
              <Typography className="font-Saira text-[15px] font-bold text-[#fff] text-left">
                {t("StockTransfer")}
              </Typography>
              <Typography className="font-Saira text-[13px] text-[#fff] text-left">
                <span className="font-bold">{t("Date")}</span>{" "}
                <span>{stockTransfer?.transferDate?.split("T")[0] || ""}</span>
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <div className="flex justify-end gap-10">
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">
                    {t("from") + " " + t("store")}
                  </span>{" "}
                  <br />
                  <span className="font-medium">
                    {stockTransfer?.sourceStoreTitle || ""}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">
                    {t("to") + " " + t("store")}
                  </span>{" "}
                  <br />
                  <span className="font-medium">
                    {stockTransfer?.destinationStoreTitle || ""}
                  </span>
                </Typography>
                <Typography className="font-Saira text-[11px] text-[#fff] text-center">
                  <span className="font-bold">{t("totalQuantity")}</span> <br />
                  <span className="font-medium">
                    {stockTransfer?.totalQuantity || 0}
                  </span>
                </Typography>
              </div>
            </Grid>
          </Grid>
        ) : null}
        {/* <Grid item xs={12}> */}
        {/* <Typography
          className="font-Saira text-[13px] leading-[16px] font-semibold text-left"
          style={{ color: themeColors?.primary }}
        >
          {t("StockTransfer")} {t("Details")}
        </Typography> */}
        {/* </Grid> */}
        <Grid
          container
          item
          xs={12}
          gap={2.5}
          className="stockTransferDetails flex-nowrap"
        >
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="sourceStoreId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("from") + " " + t("store")}
                  {...field}
                  options={storeOptions}
                  isSearchable
                  isRequired
                  defaultPlaceholder="phSelectFromStore"
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    setValue("destinationStoreId", null);
                    setDestinationBins([]);
                    setStockTransferItem((prev) =>
                      prev.map((item) => ({
                        ...item,
                        destinationBinId: null,
                      })),
                    );
                  }}
                  error={errors.sourceStoreId?.message as string}
                  value={
                    params?.id
                      ? {
                          value: stockTransfer?.sourceStoreId,
                          label: stockTransfer?.sourceStoreTitle,
                        }
                      : watch("sourceStoreId")
                  }
                  isDisabled={params?.id ? true : false}
                  isLoading={searchLoading === "stores"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions("stores", "searchIt", search);
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions("stores", "searchIt", "");
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(storesData?.pages[0]?.data as any).last)
                      handleDropdownActions("stores", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="destinationStoreId"
              control={control}
              defaultValue={null}
              rules={{ required: t("This field is required.") }}
              render={({ field }) => (
                <DynamicSelect
                  label={t("to") + " " + t("store")}
                  {...field}
                  options={destinationStores}
                  isSearchable
                  isRequired
                  defaultPlaceholder="phSelectToStore"
                  onChange={(selectedOption: any) => {
                    field.onChange(selectedOption);
                    handleDestinationStoreChange(selectedOption);
                  }}
                  error={errors.destinationStoreId?.message as string}
                  value={
                    params?.id
                      ? {
                          value: stockTransfer?.destinationStoreId,
                          label: stockTransfer?.destinationStoreTitle,
                        }
                      : watch("destinationStoreId")
                  }
                  isDisabled={
                    params?.id
                      ? true
                      : watch("sourceStoreId")?.value
                        ? false
                        : true
                  }
                  isLoading={searchLoading === "destinationStores"}
                  onInputChange={(
                    search: string,
                    { action }: { action: string },
                  ) => {
                    if (
                      action === "input-change" ||
                      (action !== "set-value" &&
                        action !== "menu-close" &&
                        action !== "input-blur")
                    )
                      handleDropdownActions(
                        "destinationStores",
                        "searchIt",
                        search,
                      );
                    else if (
                      action === "input-blur" ||
                      action === "menu-close"
                    ) {
                      if (dropdownSearchTerm || dropdownPage > 1)
                        handleDropdownActions(
                          "destinationStores",
                          "searchIt",
                          "",
                        );
                      else setDropdownSearchTerm("");
                      setDropdownPage(1);
                    }
                  }}
                  onMenuScrollToBottom={() => {
                    if (!(storesData?.pages[0]?.data as any).last)
                      handleDropdownActions("destinationStores", "loadNext");
                  }}
                  page={dropdownPage}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <Controller
              name="transferDate"
              control={control}
              rules={{
                validate: (value) => {
                  const date = new Date(value);
                  return isNaN(date.getTime()) ? t("Invalid Date") : true;
                },
              }}
              render={({ field }) => (
                <BasicDatePicker
                  type="datePicker"
                  {...field}
                  label={t("transferDate")}
                  fullWidth
                  onChange={(value: any) =>
                    field.onChange(
                      new Date(value)
                        .toLocaleDateString("en-CA", {
                          year: "numeric",
                          month: "2-digit",
                          day: "2-digit",
                        })
                        ?.split("/")
                        .reverse()
                        .join("-"),
                    )
                  }
                  required={true}
                  error={errors.transferDate?.message as string}
                  disable={params?.id}
                />
              )}
            />
          </Grid>
          <Grid item xs={12} md={4} lg={3}>
            <TextField
              {...register("remark")}
              placeholder={t("phStockTransferRemarks")}
              label={t("remarks")}
              title={t("remarks")}
              fullWidth
              error={!!errors.remark}
              helperText={errors.remark?.message as string}
              InputLabelProps={{ shrink: true }}
              disabled={(params as any)?.id}
            />
          </Grid>
        </Grid>
        {/* <Grid container> */}
        <Grid item xs={12} className="relative stockTransferDetails">
          {loading ? (
            <Loader />
          ) : (
            <Box className="bg-white py-2 px-[18px] rounded-[8px]">
              <Typography className="font-Saira text-[12px] font-semibold text-[#171826] text-left">
                {t("StockTransfer")}
              </Typography>
              <InventoryItemsTable
                form={{
                  control,
                  register,
                  handleSubmit,
                  setValue,
                  errors,
                  watch,
                  resetField,
                }}
                columns={columns}
                handleInputChange={handleInputChange}
                rows={stockTransferItem}
                typeName="stockTransferItem"
                items={itemsData?.pages[0]?.data?.content || []}
                currency={loggedInUserCurrency}
                DeleteItem={DeleteItem}
                {...{
                  params,
                  dropdownPage,
                  searchLoading,
                  dropdownSearchTerm,
                  setDropdownSearchTerm,
                  setDropdownPage,
                }}
                checkStatus={params?.id ? 2 : 1}
                viewDelete={params?.id ? false : true}
                itemsResponse={itemsData || {}}
                handleDropdownActions={handleDropdownActions}
                binOptions={destinationBins}
                onItemSelected={(index, selectedOption) => {
                  if (
                    !isDestinationBinMode ||
                    !destinationStore?.value ||
                    !selectedOption?.value
                  ) {
                    setValue(
                      `stockTransferItem.${index}.destinationBinId`,
                      null,
                    );
                    return;
                  }
                  fetchItemDestinationLocation(
                    index,
                    Number(selectedOption.value),
                    Number(destinationStore.value),
                  );
                }}
              />
              <Grid item xs={12} sm={6} className="hide-on-print">
                <div className="flex gap-2 justify-start">
                  <Button
                    className="secondary flex gap-1"
                    style={{ color: themeColors?.primary }}
                    disabled={params?.id ? true : false}
                    variant="contained"
                    disableElevation
                    onClick={() => addNewItem("stockTransferItem")}
                  >
                    <AddIcon className="w-[20px] h-[20px]" />{" "}
                    {t("Add More Items")}
                  </Button>
                  {/* <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('Import')}</Button>
                                <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t("Export")}</Button> */}
                </div>
              </Grid>
            </Box>
          )}
        </Grid>
        {/* </Grid> */}
        <Grid item xs={12} className="hide-on-print">
          <Divider className="bg-[#AEB3C4] mt-[8px]" />
        </Grid>
        <Grid container className="items-center hide-on-print">
          <Grid item xs={12} sm={6} className="text-start"></Grid>
          <Grid item xs={12} sm={6}>
            <div className="flex gap-2 justify-end">
              {params?.id ? null : (
                <Button
                  type="submit"
                  disabled={IsBtnEnabled || loading}
                  className="primary"
                  style={{ background: themeColors?.primary }}
                  variant="contained"
                  disableElevation
                  onClick={() => onSubmit(handleSubmit(onSubmit))}
                >
                  {t("save")}
                </Button>
              )}
              {params?.id ? (
                <>
                  {/*  <Button className="secondary" style={{ color: themeColors?.primary }} variant="contained" disableElevation>{t('email')}</Button> */}
                  <Button
                    className="secondary"
                    variant="contained"
                    style={{ color: themeColors?.primary }}
                    disableElevation
                    onClick={() =>
                      navigate(`${window.location.pathname}/print`)
                    }
                  >
                    {t("Print")}
                  </Button>
                </>
              ) : null}
              <Button
                className="secondary"
                variant="contained"
                style={{ color: themeColors?.primary }}
                disableElevation
                onClick={() => navigate("/inventory/stock-transfers")}
              >
                {t("Back to list")}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default StockTransferCreateUpdate;
